/** @typedef {import('swiper').Swiper} Swiper */
import {A11y, Autoplay, Navigation, Pagination} from 'swiper/modules';

import {
    MOBILE_SLIDES_PER_VIEW,
    PEEK_GRID_SLIDES_PER_VIEW,
    slidesPerViewBreakpoints,
} from './carousel.breakpoints.js';
import {DEFAULT_SLIDE_GAP_PX, prefersReducedMotion} from './carousel.util.js';

/**
 * @typedef {object} CarouselBuildContext
 * @property {HTMLElement} $host
 * @property {HTMLElement} $swiperEl
 * @property {object} settings
 * @property {string} navMode
 * @property {boolean} syncActiveSlides
 */

/**
 * @typedef {object} CarouselBuildResult
 * @property {HTMLElement|string} swiperEl
 * @property {object} swiperOptions
 * @property {import('swiper/types').SwiperModule[]} [modules]
 * @property {(swiper: Swiper | null) => void} [afterInit]
 * @property {boolean} [skipSwiper]
 */

/**
 * @param {CarouselBuildContext} ctx
 * @returns {CarouselBuildResult}
 */
export function buildLibraryDefault(ctx) {
    const {$host, settings, navMode, syncActiveSlides} = ctx;
    const mergedSettings = {...settings};

    if (prefersReducedMotion() && mergedSettings.autoplay) {
        mergedSettings.autoplay = false;
    }

    const modules = [A11y];
    const usePagination = navMode === 'inline-pagination' &&
        $host.querySelector('.pagination__bullets');
    const useNavigation = navMode !== 'none';

    if (useNavigation) {
        modules.push(Navigation);
    }
    if (usePagination) {
        modules.push(Pagination);
    }
    if (mergedSettings.autoplay) {
        modules.push(Autoplay);
    }

    const params = {
        modules,
        slidesPerView: MOBILE_SLIDES_PER_VIEW,
        spaceBetween: DEFAULT_SLIDE_GAP_PX,
        breakpoints: slidesPerViewBreakpoints(PEEK_GRID_SLIDES_PER_VIEW),
        grabCursor: true,
    };

    if (usePagination) {
        params.pagination = {
            clickable: true,
            el: $host.querySelector('.pagination__bullets'),
            renderBullet(index, className) {
                return `<button type="button" class="pagination__link type-eyebrow-2 ${className}" aria-label="Slide ${index + 1}">${index + 1}</button>`;
            },
        };
    }

    if (useNavigation) {
        const $prev = $host.querySelector('.pagination__arrow-left .previous');
        const $next = $host.querySelector('.pagination__arrow-right .next');

        params.navigation = {};

        if (navMode !== 'next-only' && $prev) {
            params.navigation.prevEl = $prev;
        }

        if (
            (navMode === 'inline-pagination' || navMode === 'next-only') &&
            $next
        ) {
            params.navigation.nextEl = $next;
        }
    }

    if (syncActiveSlides) {
        params.watchSlidesProgress = true;
    }

    const swiperOptions = {...params, ...mergedSettings};

    if (syncActiveSlides) {
        const contentSelector =
            $host.dataset.syncContentSelector || '[data-carousel-slide-content]';
        const syncActive = () => syncActiveSlideContent($host, contentSelector);
        swiperOptions.on = {
            ...(swiperOptions.on || {}),
            init: syncActive,
            slideChange: syncActive,
        };
    }

    return {
        swiperEl: `#${$host.id}`,
        swiperOptions,
        modules,
    };
}

/**
 * @param {HTMLElement} $host
 * @param {string} contentSelector
 */
function syncActiveSlideContent($host, contentSelector) {
    const $slides = $host.querySelectorAll('.swiper-slide');
    $slides.forEach(($slide) => {
        const isActive = $slide.classList.contains('swiper-slide-active');
        $slide.classList.toggle('is-active', isActive);

        $slide.querySelectorAll(contentSelector).forEach(($el) => {
            $el.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });
    });
}

/** @type {Record<string, (ctx: CarouselBuildContext) => CarouselBuildResult>} */
export const builtinPresets = {
    'library-default': buildLibraryDefault,
};
